import { Box, CSSObject, styled } from "@mui/material";
import PageBannerProps, {
  PageBannerAlignment,
} from "../../../interfaces/props/PageBannerProps";
import { PositionTokensEnum } from "../../../../enums/tokens/positionTokenEnum";
import { ColorTokensEnum } from "../../../../enums/tokens/colorTokensEnum";
import { BannerVariantEnum } from "../../../../enums/tokens/bannerVariantEnum";
import { TypographyTokensEnum } from "../../../../enums/tokens/typographyTokensEnum";
import { brandColors } from "../../../../stories/helpers/ColorTokenHelper";
import typographyTokens from "../../../../stories/tokens/typography-tokens.json";

/**
 * Figma "Page banner" (LansingNOW Design System), from its CSS export:
 * `height: 84px; padding: 24px 0; justify-content: center; align-items:
 * center; gap: 8px` — no border. 84 − 24 − 24 leaves a 36px content row,
 * which is Heading 1 (36px, line-height 1.0). The IMPACT centred logo and the
 * checkout header carry taller content and use 12px instead. The content row
 * is capped at 1300px with 32px side padding. The height is a minimum, not a
 * clamp: a label that wraps on a narrow screen grows the bar instead of
 * overflowing it.
 */
const MIN_HEIGHT_PX = 84;
const PADDING_Y_PX = 24;
const PADDING_Y_TALL_CONTENT_PX = 12;
const CONTENT_MAX_WIDTH_PX = 1300;
const PADDING_X_PX = 32;
const PADDING_X_MOBILE_PX = 16;
const GAP_PX = 8;
/** The IMPACT centred logo fills its row: 84 − 12 − 12. */
const LOGO_HEIGHT_PX = 60;

/**
 * The space-between alignment is the checkout page header (Figma: "Align:
 * space-between, Variant: Default"). Its logo is a fixed lockup rather than a
 * scaled mark — Card Payments & Management spec §1.0 ("Logo: LansingNOW
 * all-white logo at 175 × 42"), which Figma's 43px content row agrees with.
 */
const HEADER_LOGO_WIDTH_PX = 175;
const HEADER_LOGO_HEIGHT_PX = 42;
const HEADER_LOGO_HEIGHT_MOBILE_PX = 32;

/**
 * Everything a banner label needs regardless of alignment. It is spread rather
 * than inherited because a block that restyles `& .MuiTypography-root`
 * **replaces** that key — object spread does not deep-merge — so a block
 * that forgot `color` would drop the label to the default dark text on a
 * coloured bar. Spreading this first makes that impossible.
 */
const LABEL_BASE: CSSObject = {
  color: "white",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const justifyFor = (alignment: PageBannerAlignment) => {
  switch (alignment) {
    case PositionTokensEnum.CENTER:
      return "center";
    case PositionTokensEnum.SPACE_BETWEEN:
      return "space-between";
    default:
      return "flex-start";
  }
};

/**
 * The fill belongs to the variant, never the caller: Default is `brand-dark`,
 * IMPACT is `accent`. There is no `backgroundColor` prop.
 */
const BACKGROUND_FOR: Record<BannerVariantEnum, string> = {
  [BannerVariantEnum.DEFAULT]: brandColors[ColorTokensEnum.BRAND_DARK],
  [BannerVariantEnum.IMPACT]: brandColors[ColorTokensEnum.ACCENT],
};

type OwnerState = {
  resolvedBg: string;
  resolvedAlign: PageBannerAlignment;
  paddingY: number;
  isHeader: boolean;
  isImpactHeading: boolean;
};

const BannerContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerState",
})<{ ownerState: OwnerState }>(({ theme, ownerState }) => {
  const heading1 = (typographyTokens[TypographyTokensEnum.HEADING_1] ||
    {}) as CSSObject;
  const heading2 = (typographyTokens[TypographyTokensEnum.HEADING_2] ||
    {}) as CSSObject;
  const heading3 = (typographyTokens[TypographyTokensEnum.HEADING_3] ||
    {}) as CSSObject;
  const heading4 = (typographyTokens[TypographyTokensEnum.HEADING_4] ||
    {}) as CSSObject;
  return {
    display: "flex",
    alignItems: "center",
    width: "100%",
    boxSizing: "border-box",
    minHeight: `${MIN_HEIGHT_PX}px`,
    padding: `${ownerState.paddingY}px 0`,
    backgroundColor: ownerState.resolvedBg,

    "& img": {
      display: "block",
      height: `${LOGO_HEIGHT_PX}px`,
      width: "auto",
    },

    // Figma's "Page heading" fills the 36px row: Heading 1 (36px × 1.0),
    // stepping down to Heading 3 on a tablet and Heading 4 on a phone.
    "& .MuiTypography-root": {
      ...LABEL_BASE,
      ...heading1,
      whiteSpace: "normal",
      textAlign:
        ownerState.resolvedAlign === PositionTokensEnum.CENTER
          ? "center"
          : "left",

      [theme.breakpoints.down("md")]: {
        ...heading3,
      },
      [theme.breakpoints.down("sm")]: {
        ...heading4,
      },
    },

    ...(ownerState.isImpactHeading && {
      "& .MuiTypography-root": {
        ...LABEL_BASE,
        ...heading1,
        whiteSpace: "normal",
        textAlign:
          ownerState.resolvedAlign === PositionTokensEnum.CENTER
            ? "center"
            : "left",
        [theme.breakpoints.down("sm")]: {
          ...heading2,
          textAlign: "center",
        },
      },
      [theme.breakpoints.down("sm")]: {
        "& > .MuiBox-root": {
          justifyContent: "center",
        },
      },
    }),

    // --- ALIGN: SPACE-BETWEEN (the checkout header) ----------------------
    // Last in the object so it beats the rules above at equal specificity.
    // Scoped entirely to this alignment: left and center are untouched.
    ...(ownerState.isHeader && {
      // **The height drives the width, never the other way round.** Forcing
      // both (with `object-fit: contain`) letterboxes any mark whose aspect
      // ratio is not exactly 175 ∶ 42 — the horizontal logo that used to sit
      // here is 8.9 ∶ 1 and drew at half the height inside the box. The
      // stacked lockup is 3.97 ∶ 1, so 42px tall lands at ~167px wide, inside
      // the 175px the spec states as the width.
      "& img": {
        display: "block",
        width: "auto",
        maxWidth: `${HEADER_LOGO_WIDTH_PX}px`,
        height: `${HEADER_LOGO_HEIGHT_PX}px`,
      },

      // The label sits beside a logo and stays on one line, right-aligned.
      "& .MuiTypography-root": {
        ...LABEL_BASE,
        ...heading3,
        whiteSpace: "nowrap",
        textAlign: "right",
        [theme.breakpoints.down("sm")]: {
          ...heading4,
        },
      },

      // The logo is half this banner's content — it scales down on a phone
      // rather than disappearing.
      [theme.breakpoints.down("sm")]: {
        "& img": {
          height: `${HEADER_LOGO_HEIGHT_MOBILE_PX}px`,
        },
      },
    }),
  };
});

export const PageBanner = ({
  children,
  variant = BannerVariantEnum.DEFAULT,
  contentAlignment = PositionTokensEnum.LEFT,
}: PageBannerProps) => {
  const isHeader = contentAlignment === PositionTokensEnum.SPACE_BETWEEN;
  // A centred IMPACT banner carries the IMPACT logo, and the checkout header
  // a 42px lockup; both are taller than a heading, so Figma halves the padding.
  const tallContent =
    isHeader ||
    (variant === BannerVariantEnum.IMPACT &&
      contentAlignment === PositionTokensEnum.CENTER);

  return (
    <BannerContainer
      ownerState={{
        resolvedBg:
          BACKGROUND_FOR[variant] ?? BACKGROUND_FOR[BannerVariantEnum.DEFAULT],
        resolvedAlign: contentAlignment,
        paddingY: tallContent ? PADDING_Y_TALL_CONTENT_PX : PADDING_Y_PX,
        isHeader,
        isImpactHeading: variant === BannerVariantEnum.IMPACT && !tallContent,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: justifyFor(contentAlignment),
          gap: `${GAP_PX}px`,
          width: "100%",
          maxWidth: `${CONTENT_MAX_WIDTH_PX}px`,
          margin: "0 auto",
          boxSizing: "border-box",
          px: { xs: `${PADDING_X_MOBILE_PX}px`, sm: `${PADDING_X_PX}px` },
        }}
      >
        {children}
      </Box>
    </BannerContainer>
  );
};
